import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Download, Printer, ArrowLeft, Loader2, Sparkles, AlertTriangle, FileSpreadsheet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { exportElementToPdf } from "@/lib/exportPdf";

import GrowthScoreCard from "@/components/plan/GrowthScoreCard";
import TodaysActionsCard from "@/components/plan/TodaysActionsCard";
import PlanningScenarios from "@/components/plan/PlanningScenarios";
import QuickWinsSection from "@/components/plan/QuickWinsSection";
import TargetAudienceSection from "@/components/plan/TargetAudienceSection";
import KeywordSection from "@/components/plan/KeywordSection";
import GoogleAdsSection from "@/components/plan/GoogleAdsSection";
import MetaAdsSection from "@/components/plan/MetaAdsSection";
import LandingSection from "@/components/plan/LandingSection";
import ContentCalendarSection from "@/components/plan/ContentCalendarSection";
import RetargetingSection from "@/components/plan/RetargetingSection";
import WeeklyChecklistSection from "@/components/plan/WeeklyChecklistSection";
import KpiSection from "@/components/plan/KpiSection";
import PricingSection from "@/components/PricingSection";

export default function PlanView() {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [xlsLoading, setXlsLoading] = useState(false);
  const [scenario, setScenario] = useState("expected");
  const [checked, setChecked] = useState({});

  useEffect(() => {
    let cancelled = false;
    api.get(`/plans/${id}`)
      .then((r) => { if (!cancelled) setPlan(r.data); })
      .catch(() => { if (!cancelled) toast.error("Plan not found"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    try {
      const saved = JSON.parse(localStorage.getItem(`lg_checked_${id}`) || "{}");
      if (!cancelled) setChecked(saved);
    } catch {
      // Corrupted localStorage — silently ignore and start with empty state.
    }
    return () => { cancelled = true; };
  }, [id]);

  const toggle = (key) => {
    setChecked((c) => {
      const n = { ...c, [key]: !c[key] };
      localStorage.setItem(`lg_checked_${id}`, JSON.stringify(n));
      return n;
    });
  };

  const onProofChange = (i, next, alreadyChecked) => {
    setPlan((prev) => {
      const proofs = { ...(prev?.action_proofs || {}) };
      if (next) proofs[String(i)] = next;
      else if (proofs[String(i)]) proofs[String(i)] = { ...proofs[String(i)], is_deleted: true };
      return { ...prev, action_proofs: proofs };
    });
    const key = `ta_${i}`;
    if (next && !alreadyChecked) {
      // First upload: mark done
      toggle(key);
    } else if (!next && alreadyChecked) {
      // Delete: untick so state stays consistent with the emptied slot
      toggle(key);
    }
  };

  const exportPDF = async () => {
    setPdfLoading(true);
    try {
      await exportElementToPdf("plan-content", `leadgen-plan-${plan?.input?.business_name || "plan"}.pdf`);
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setPdfLoading(false);
    }
  };

  const exportExcel = async () => {
    setXlsLoading(true);
    try {
      const { data, headers } = await api.get(`/plans/${id}/export/excel`, { responseType: "blob" });
      const name = (headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "leadgen-plan.xlsx";
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Excel export failed");
    } finally {
      setXlsLoading(false);
    }
  };

  const refreshPlan = async () => {
    const { data } = await api.get(`/plans/${id}`);
    setPlan(data);
  };

  const unlockDemo = async () => {
    try {
      await api.post(`/plans/${id}/unlock-demo`);
      const { data } = await api.get(`/plans/${id}`);
      setPlan(data);
      toast.success("Demo unlock: all pro sections revealed.");
    } catch {
      toast.error("Could not unlock");
    }
  };

  if (loading) return <div className="min-h-screen bg-zinc-50"><Header /><div className="py-24 text-center text-zinc-500">Loading your plan…</div></div>;
  if (!plan) return <div className="min-h-screen bg-zinc-50"><Header /><div className="py-24 text-center">Plan not found</div></div>;

  const p = plan.plan || {};
  const inp = plan.input || {};
  const isFree = !plan.paid;
  const proofsMap = plan.action_proofs || {};

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        {/* Toolbar */}
        <div className="flex flex-wrap justify-between gap-3 items-center no-print">
          <Link to="/plans" data-testid="back-to-plans" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900">
            <ArrowLeft className="h-4 w-4" /> All plans
          </Link>
          <div data-testid="plan-toolbar-actions" className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 w-full sm:w-auto">
            {isFree && (
              <Button data-testid="btn-unlock-demo" variant="outline" onClick={unlockDemo} className="col-span-2 sm:col-span-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                <Sparkles className="h-4 w-4 mr-2" />Demo unlock
              </Button>
            )}
            <Button variant="outline" data-testid="btn-print" onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" />Print</Button>
            {!isFree && (
              <Button variant="outline" data-testid="btn-download-excel" onClick={exportExcel} disabled={xlsLoading} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                {xlsLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Building…</> : <><FileSpreadsheet className="h-4 w-4 mr-2" />Excel</>}
              </Button>
            )}
            <Button data-testid="btn-download-pdf" onClick={exportPDF} disabled={pdfLoading} className={`${isFree ? "" : "col-span-2 sm:col-span-1 "}bg-indigo-600 hover:bg-indigo-700`}>
              {pdfLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Building PDF…</> : <><Download className="h-4 w-4 mr-2" />Download PDF</>}
            </Button>
          </div>
        </div>

        <div id="plan-content" className="mt-8">
          {/* Header */}
          <div className="bg-zinc-900 text-white rounded-3xl p-6 sm:p-10">
            <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-widest text-indigo-300">
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Your marketing operating system
              </span>
              {plan.model && (
                <span data-testid="plan-model-badge" className="inline-flex items-center gap-1.5 bg-white/10 border border-white/15 rounded-full px-2.5 py-0.5 tracking-normal normal-case text-[11px] text-zinc-200">
                  Built with {plan.model === "chatgpt" ? "ChatGPT · GPT 5.6 Terra" : "Claude Sonnet 4.6"}
                </span>
              )}
            </div>
            <h1 data-testid="plan-title" className="font-display font-black text-4xl md:text-6xl leading-[1.1] tracking-tight mt-4 break-words">
              {inp.business_name}
            </h1>
            <p className="text-zinc-400 mt-2 break-words">
              {inp.business_type} · {inp.location} · Budget ₹{Number(inp.monthly_budget).toLocaleString("en-IN")}/mo
              {inp.website_url && (
                <> · <a href={inp.website_url} target="_blank" rel="noreferrer" className="underline decoration-dotted">
                  {(() => { try { return new URL(inp.website_url).host; } catch { return inp.website_url; } })()}
                </a></>
              )}
            </p>
            {p.executive_summary && (
              <p className="mt-6 text-base sm:text-lg text-zinc-200 max-w-3xl leading-relaxed">{p.executive_summary}</p>
            )}
          </div>

          {p.illustrative_disclaimer && (
            <div data-testid="illustrative-banner" className="mt-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-amber-600" />
              <div>
                <span className="font-medium">Illustrative planning scenarios · not guarantees.</span>{" "}
                {p.illustrative_disclaimer}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            <GrowthScoreCard score={p.growth_score} />
            <TodaysActionsCard
              actions={p.todays_actions}
              planId={id}
              checked={checked}
              proofsMap={proofsMap}
              onToggle={toggle}
              onProofChange={onProofChange}
            />
          </div>

          <PlanningScenarios
            scenarios={p.planning_scenarios}
            assumptions={p.assumptions_summary}
            scenario={scenario}
            setScenario={setScenario}
            isFree={isFree}
          />
          <QuickWinsSection wins={p.quick_wins} />
          <TargetAudienceSection audience={p.target_audience} isFree={isFree} />
          <KeywordSection strategy={p.keyword_strategy} isFree={isFree} />
          <GoogleAdsSection ads={p.google_ads} isFree={isFree} />
          <MetaAdsSection ads={p.meta_ads} locked={isFree} />
          <LandingSection landing={p.landing_page} locked={isFree} />
          <ContentCalendarSection calendar={p.content_calendar} isFree={isFree} />
          <RetargetingSection retargeting={p.retargeting_plan} locked={isFree} />
          <WeeklyChecklistSection
            checklist={p.weekly_optimization_checklist}
            locked={isFree}
            checked={checked}
            onToggle={toggle}
          />
          <KpiSection dashboard={p.kpi_dashboard} locked={isFree} />

          {isFree && (
            <div className="mt-14 bg-zinc-900 text-white rounded-3xl p-6 sm:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-indigo-300">Unlock the full operating system</div>
                <h3 className="font-display font-black text-3xl md:text-4xl mt-2">All the assets to ship every move</h3>
                <p className="text-zinc-400 mt-2">Meta ads, retargeting, KPIs, weekly checklist and the full 30-day calendar. Pay once. Own it forever.</p>
              </div>
              <div className="flex gap-3">
                <a href="#pricing" data-testid="see-pricing-bottom" className="btn-lift inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-zinc-900 font-medium hover:bg-indigo-500 hover:text-white">
                  Pick a package
                </a>
              </div>
            </div>
          )}
        </div>
        {isFree && (
          <div className="no-print">
            <PricingSection planId={id} compact onPaid={refreshPlan} />
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
